import { call, put, select, takeEvery, takeLatest } from "redux-saga/effects";
import sendEmailUsingGraphSaga, { sendCancellationEmailsUsingGraphSaga } from "../../helpers/graphHelpers";
import axios from "axios";
import { describeError } from "../../lib/apiError";
import authSaga from "./authSaga";

const API_URL = "https://api.screencom.ch";

/**
 * Versand gescheitert — beide Wege benennen. Welcher von beiden weggebrochen
 * ist, entscheidet, wo der Support sucht: Microsoft 365 (Graph, im Namen der
 * angemeldeten Person) oder der eigene Versand als Rückfall.
 */
const mailFailure = (graphError, acsError) => ({
  type: "SEND_EMAIL_FAILURE",
  payload: `Microsoft 365: ${describeError(graphError)} · Rückfall: ${describeError(acsError)}`,
});

const getCustomerName = (state) => state.company.data.customer_name;
const getUserToken = (state) => state.user.token;

function* fetchAccessToken() {
  let token = yield select(getUserToken);

  if (!token) {
    token = yield call(authSaga, { type: "GET_COMPANY_DATA_REQUEST" });
    if (token) {
      yield put({ type: "SET_TOKEN", payload: token });
    } else {
      throw new Error("Failed to fetch access token for user permissions.");
    }
  }

  return token;
}

function* sendEmailUsingACSSaga(visitors, customerName, emailSubject) {
  try {
    const token = yield call(fetchAccessToken);

    for (const visitor of visitors) {
      const email = {
        recipient_mail: visitor.email,
        recipient_name: visitor.name || "Visitor",
        subject: visitor.email_subject || emailSubject,
        content: visitor.email_with_qr_code,
        qr_code: visitor.qr_code_base64,
        logo: visitor.logo_base64,
      };

      const url = "https://api.screencom.ch/user/mail";
      const response = yield call(axios.post, url, email, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          accept: "application/json",
        },
      });

      if (response.status !== 200) {
        const errorText = response.statusText;
        console.error("Fehler beim Senden der E-Mail über die ACS API:", errorText);
        throw new Error(`ACS API Fehler: ${response.status}`);
      }
    }
  } catch (error) {
    console.error("Fehler bei der ACS API oder beim E-Mail-Versand", error);
    throw error;
  }
}

function* sendCancellationEmailsUsingACSSaga(cancellationEmails) {
  try {
    const token = yield call(fetchAccessToken);

    for (const mail of cancellationEmails) {
      const email = {
        recipient_mail: mail.email,
        recipient_name: mail.visitor_name || "Visitor",
        subject: mail.subject,
        content: mail.html,
      };

      const url = "https://api.screencom.ch/user/mail";
      const response = yield call(axios.post, url, email, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          accept: "application/json",
        },
      });

      if (response.status !== 200) {
        const errorText = response.statusText;
        console.error("Fehler beim Senden der Absage-E-Mail über die ACS API:", errorText);
        throw new Error(`ACS API Fehler: ${response.status}`);
      }
    }
  } catch (error) {
    console.error("Fehler bei der ACS API oder beim Absage-E-Mail-Versand", error);
    throw error;
  }
}

/**
 * Storno-Mails versenden.
 *
 * Ausgelöst beim Löschen eines Termins und beim Entfernen eines einzelnen
 * Teilnehmers; beide Sagas reichen die Mails als `cancellation_emails` weiter.
 */
function* sendCancellationEmails(action) {
  try {
    const cancellationEmails = action.payload?.cancellation_emails;

    if (!Array.isArray(cancellationEmails) || cancellationEmails.length === 0) {
      return;
    }

    try {
      yield call(sendCancellationEmailsUsingGraphSaga, cancellationEmails);
      yield put({ type: "SEND_EMAIL_SUCCESS" });
    } catch (graphError) {
      console.error("Graph API failed, trying ACS:", graphError);
      try {
        yield call(sendCancellationEmailsUsingACSSaga, cancellationEmails);
        yield put({ type: "SEND_EMAIL_SUCCESS" });
      } catch (acsError) {
        console.error("Both Graph API and ACS failed:", acsError);
        yield put(mailFailure(graphError, acsError));
      }
    }
  } catch (error) {
    console.error("Failed to send cancellation email:", error);
    yield put({ type: "SEND_EMAIL_FAILURE", payload: describeError(error) });
  }
}

function* sendEmail(action) {
  try {
    const customerName = yield select(getCustomerName);

    if (action.payload[0] === true) {
      const visitors = action.payload[2].visitors;

      const emailSubject = action.payload[2].email_subject || `Sie sind zu ${customerName} eingeladen`;

      try {
        yield call(sendEmailUsingGraphSaga, visitors, customerName, emailSubject);
        yield put({ type: "SEND_EMAIL_SUCCESS" });
      } catch (graphError) {
        console.error("Graph API failed, trying ACS:", graphError);
        try {
          yield call(sendEmailUsingACSSaga, visitors, customerName, emailSubject);
          yield put({ type: "SEND_EMAIL_SUCCESS" });
        } catch (acsError) {
          console.error("Both Graph API and ACS failed:", acsError);
          yield put(mailFailure(graphError, acsError));
        }
      }
    }
  } catch (error) {
    console.error("Failed to send email:", error);
    yield put({ type: "SEND_EMAIL_FAILURE", payload: describeError(error) });
  }
}

/**
 * Einladung eines Teilnehmers erneut versenden.
 *
 * Das Backend liefert die fertig gerenderte Mail (`/api/invite_content`) —
 * dieselbe Struktur wie beim Anlegen eines Termins. Verschickt wird sie über
 * denselben Weg wie sonst: Graph im Namen des Organisators, ACS als Rückfall.
 */
function* resendInvite(action) {
  const { outlook_id, visitor_id, onDone } = action.payload || {};
  try {
    const customerName = yield select(getCustomerName);
    // Denselben Weg nehmen wie die übrigen Backend-Aufrufe: `fetchAccessToken`
    // greift nur auf ein bereits im Store liegendes Token zurück und scheitert,
    // wenn der SSO-Weg auf MSAL umgestellt wurde.
    const token = yield call(authSaga, { type: "RESEND_INVITE_REQUEST" });
    if (!token) {
      throw new Error("Kein Zugriffstoken für den erneuten Versand");
    }

    const res = yield call(
      axios.post,
      `${API_URL}/api/invite_content`,
      { outlook_id, visitor_ids: visitor_id ? [visitor_id] : undefined },
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          accept: "application/json",
        },
      },
    );

    const visitors = res.data?.data?.visitors || [];
    if (!visitors.length) {
      throw new Error("Keine Einladung erhalten");
    }
    const subject = res.data?.data?.email_subject || `Sie sind zu ${customerName} eingeladen`;

    try {
      yield call(sendEmailUsingGraphSaga, visitors, customerName, subject);
    } catch (graphError) {
      console.error("Graph API failed, trying ACS:", graphError);
      yield call(sendEmailUsingACSSaga, visitors, customerName, subject);
    }

    yield put({ type: "RESEND_INVITE_SUCCESS", payload: { visitor_id } });
    if (onDone) onDone(true);
  } catch (error) {
    console.error("Failed to resend invitation:", error);
    yield put({ type: "RESEND_INVITE_FAILURE", payload: { visitor_id } });
    if (onDone) onDone(false);
  }
}

/**
 * Umbuchungs-Mails versenden.
 *
 * Inhalt kommt fertig vom Backend (Vorlage `reschedule`); der Versandweg ist
 * derselbe wie bei den Stornierungen.
 */
function* sendRescheduleMails(action) {
  const mails = action.payload?.mails || [];
  if (!mails.length) return;
  try {
    yield call(sendCancellationEmailsUsingGraphSaga, mails);
    yield put({ type: "SEND_EMAIL_SUCCESS" });
  } catch (graphError) {
    console.error("Graph API failed, trying ACS:", graphError);
    try {
      yield call(sendCancellationEmailsUsingACSSaga, mails);
      yield put({ type: "SEND_EMAIL_SUCCESS" });
    } catch (acsError) {
      console.error("Both Graph API and ACS failed:", acsError);
      yield put(mailFailure(graphError, acsError));
    }
  }
}

export function* watchCreateAppointmentSuccess() {
  yield takeLatest("CREATE_APPOINTMENT_SUCCESS", sendEmail);
  yield takeLatest("ADD_VISITOR_TO_APPOINTMENT_SUCCESS", sendEmail);
  yield takeLatest("DELETE_APPOINTMENT_SUCCESS", sendCancellationEmails);
  yield takeEvery("DELETE_VISITOR_FROM_APPOINTMENT_SUCCESS", sendCancellationEmails);
  yield takeEvery("RESEND_INVITE_REQUEST", resendInvite);
  yield takeEvery("SEND_RESCHEDULE_MAILS", sendRescheduleMails);
}
